/**
 * US phone-number helpers shared across the coupon auth + profile surfaces.
 *
 * Internally we store the raw 10-digit national number (no country code).
 * - `normalizePhoneDigits` — strip to the 10-digit national number.
 * - `formatPhoneDisplay`   — render digits as "(555) 123-4567" (partial-friendly).
 * - `isValidPhone`         — true when exactly 10 digits are present.
 * - `toE164`               — "+1" + 10 digits for the Loyalty Lane API.
 */

/** Strip any input down to the 10-digit US national number (drops a leading "1"). */
export function normalizePhoneDigits(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return '';
  let digits = String(value).replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) {
    digits = digits.slice(1);
  }
  return digits.slice(0, 10);
}

/** Format digits as a US phone number, formatting progressively as the user types. */
export function formatPhoneDisplay(value: string | number | null | undefined): string {
  const digits = normalizePhoneDigits(value);
  if (digits.length === 0) return '';
  if (digits.length < 4) return `(${digits}`;
  if (digits.length < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

/** True when the value contains a complete 10-digit US phone number. */
export function isValidPhone(value: string | number | null | undefined): boolean {
  return normalizePhoneDigits(value).length === 10;
}

/** Convert to E.164 ("+1XXXXXXXXXX") for the Loyalty Lane API. Empty in, empty out. */
export function toE164(value: string | number | null | undefined): string {
  const digits = normalizePhoneDigits(value);
  return digits.length === 10 ? `+1${digits}` : '';
}
